import { ARENA_H, ARENA_W, BAR } from "../config.ts";
import { LevelBuilder } from "./builder.ts";
import { EN_ARCHER, EN_METEOR } from "../entities/enemy.ts";
import { gapWall, sweepBar } from "./patterns.ts";
import type { Level } from "./levelTypes.ts";

/**
 * LEVEL 1 — "INERTIA"
 * Pure evasion with a first taste of the enemy roster. Short, readable bars
 * sweep across the field one at a time, so the player learns to route around
 * each individually. Bars come in three speed tiers (see BAR.speeds) and the
 * mix escalates from lone slow bars to a brisk stream cycling all three speeds.
 * Occasional wide-gap walls add structure without ever walling off the arena.
 * Simple vertical meteors and an archer ease in the offense to read.
 *
 * (Vertical-slice length ~80s; expandable to the full 4-minute phase later.)
 */
export function buildLevel1(): Level {
  const b = new LevelBuilder(1, "INERTIA", "Evasion", 80);
  b.cue(0, "phase:1");

  // --- Intro: lone slow bars, one at a time, alternating axis (0-16s) ---
  b.every(2, 15, 2.2, (ctx, t) => {
    const k = Math.round((t - 2) / 2.2);
    const axis = k % 2 === 0 ? "h" : "v";
    const pos = 0.22 + (k % 4) * 0.18; // walk the entry lane across the field
    sweepBar(ctx, axis, pos, 0, k % 2 === 0); // tier 0 = slow
  });

  // --- Build: slow + medium singles, one occasional wide-gap wall (16-38s) ---
  b.cue(16, "intensity:1");
  b.every(16, 37, 1.7, (ctx, t) => {
    const k = Math.round((t - 16) / 1.7);
    const axis = k % 2 === 0 ? "h" : "v";
    const pos = 0.15 + ((k * 0.17) % 0.7);
    sweepBar(ctx, axis, pos, k % 2, k % 3 !== 0); // tiers 0/1
  });
  b.every(21, 36, 5, (ctx) => gapWall(ctx, "h", 0.5, 300, 34, BAR.speeds[0], true));

  // First contact with enemies. Meteors start SIMPLE here: slow, strictly
  // vertical falls in fixed lanes — pure Level-1 axis-aligned reading.
  b.every(20, 44, 8, (ctx, t) => {
    const k = Math.round((t - 20) / 8);
    ctx.enemy(EN_METEOR, ARENA_W * (0.22 + k * 0.28), -50, { vx: 0, vy: 140 });
  });

  // --- Mid: medium + fast singles, tighter cadence (38-60s) ---
  b.cue(38, "intensity:2");
  b.every(38, 59, 1.3, (ctx, t) => {
    const k = Math.round((t - 38) / 1.3);
    const axis = k % 2 === 0 ? "v" : "h";
    const pos = 0.15 + ((k * 0.23) % 0.7);
    const len = k % 3 === 0 ? 240 : BAR.length; // vary length a touch for variety
    sweepBar(ctx, axis, pos, 1 + (k % 2), k % 2 === 0, len); // tiers 1/2
  });
  // An archer octopod takes a post up top and drills its 3-dart volleys.
  b.at(48, (ctx) => {
    ctx.enemy(EN_ARCHER, ARENA_W * 0.5, -40, { homeX: ARENA_W * 0.5, homeY: ARENA_H * 0.2 });
  });

  // --- Climax: brisk stream cycling all three speeds (60-73s) ---
  b.cue(60, "intensity:3");
  b.every(60, 73, 1, (ctx, t) => {
    const k = Math.round((t - 60) / 1);
    const axis = k % 2 === 0 ? "h" : "v";
    const pos = 0.12 + ((k * 0.29) % 0.76);
    sweepBar(ctx, axis, pos, k % 3, k % 2 === 1); // cycle tiers 0/1/2
  });

  // Climax meteors: still vertical, but quicker and paired.
  b.every(66, 72, 5, (ctx, t) => {
    const k = Math.round((t - 66) / 5);
    ctx.enemy(EN_METEOR, ARENA_W * (0.3 + k * 0.2), -50, { vx: 0, vy: 190 });
    ctx.enemy(EN_METEOR, ARENA_W * (0.7 - k * 0.2), -50, { vx: 0, vy: 190 });
  });

  // Grand finale: three archers post up — both flanks and up top center — and
  // drill volleys through the closing stream; their last parting arrows fly as
  // the level winds down.
  b.at(61, (ctx) => {
    ctx.enemy(EN_ARCHER, -40, ARENA_H * 0.24, { homeX: ARENA_W * 0.2, homeY: ARENA_H * 0.24 });
    ctx.enemy(EN_ARCHER, ARENA_W + 40, ARENA_H * 0.16, {
      homeX: ARENA_W * 0.8,
      homeY: ARENA_H * 0.16,
    });
    ctx.enemy(EN_ARCHER, ARENA_W * 0.5, -40, { homeX: ARENA_W * 0.5, homeY: ARENA_H * 0.12 });
  });

  // --- Ramp down (73-80s): nothing new spawns; the last bars cross and exit
  // while the music strips back to the pad, so the level breathes out before
  // the clear banner instead of cutting off at full intensity.
  b.cue(73.5, "phase:clear");
  return b.build();
}
